import React, { useState } from "react";
import {
  Typography,
  TextField,
  useTheme,
  Grid,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import * as Yup from "yup";
import "./SecuritySettings.less";

import { useChangePasswordMutation } from "../../../../redux/features/auth/authApi";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormik } from "formik";
import RowContainer from "../../../common/RowContainer";
import ColumnContainer from "../../../common/ColumnContainer";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { DashboardButton } from "../../../common/DashboardButton";
import { useAuth } from "../../../../utils/hooks/useAuth";

const SecuritySettings = ({ onPasswordChangeStatus }) => {
  //INIT
  const theme = useTheme();
  const intl = useIntl();

  //REDUX
  const [changePassword, { isLoading, isError, isSuccess }] =
    useChangePasswordMutation();

  const { auth } = useAuth();

  //STATE
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState([
    { id: "newPassword", show: false },
    { id: "confirmPassword", show: false },
  ]);

  //FUNCTIONS
  const handleChangePasswordSubmit = async (values) => {
    try {
      await changePassword({
        token: auth.token,
        changePasswordData: {
          password: values.newPassword,
        },
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleClickShowPassword = (id) => {
    setShowPassword((prevState) => {
      return prevState.map((passwordField) =>
        passwordField.id === id
          ? { ...passwordField, show: !passwordField.show }
          : passwordField
      );
    });
  };

  //FORMIK
  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        intl.formatMessage({ id: "password.requirements" })
      )

      .min(8, <FormattedMessage id="password.minimum" />)
      .required(<FormattedMessage id="register.form.warnings.empty" />),
    confirmPassword: Yup.string().oneOf(
      [Yup.ref("newPassword"), null],
      <FormattedMessage id="password.match" />
    ),
  });

  const formik = useFormik({
    initialValues: formData,
    validationSchema: validationSchema,
    onSubmit: handleChangePasswordSubmit,
  });

  const formItems = [
    {
      label: <FormattedMessage id="profile.createNewPassword" />,
      type: showPassword.find((item) => item.id === "newPassword").show
        ? "text"
        : "password",
      name: "newPassword",
      xs: 12,
      sm: 12,
      md: 6,
    },
    {
      label: <FormattedMessage id="profile.confirmNewPassword" />,
      type: showPassword.find((item) => item.id === "confirmPassword").show
        ? "text"
        : "password",
      name: "confirmPassword",
      xs: 12,
      sm: 12,
      md: 6,
    },
  ];

  return (
    <ColumnContainer>
      <form onSubmit={formik.handleSubmit}>
        <ColumnContainer>
          <Grid container spacing={3} rowSpacing={8} mt={2}>
            {formItems.map((item, index) => (
              <Grid item xs={item.xs} sm={item.sm} height="fit-content">
                <Typography variant="p" component="p" className="input-label">
                  {item.label}
                </Typography>
                <TextField
                  size="small"
                  autoComplete="off"
                  required
                  fullWidth
                  type={item.type}
                  name={item.name}
                  value={formik.values[item.name]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched[item.name] &&
                    Boolean(formik.errors[item.name])
                  }
                  className="input-field"
                  helperText={
                    formik.touched[item.name] && formik.errors[item.name]
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => handleClickShowPassword(item.name)}
                          onMouseDown={() => handleClickShowPassword(item.name)}
                        >
                          {showPassword.find(
                            (passwordField) => passwordField.id === item.name
                          ).show ? (
                            <Visibility />
                          ) : (
                            <VisibilityOff />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12} sx={{ overflow: "hidden" }}>
            {isError ? (
              <Typography
                className="error-message"
                paragraph
                style={{
                  color: theme.palette.error.main,
                }}
              >
                <FormattedMessage id="password.error.reset" />
              </Typography>
            ) : null}
          </Grid>
          <Grid container xs={12} justifyContent="flex-end">
            <RowContainer end>
              <DashboardButton
                type="filled"
                disabled={isLoading || !formik.isValid}
              >
                {isLoading ? (
                  <CircularProgress color="inherit" size={"30px"} />
                ) : (
                  <Typography variant="p" component="p">
                    <FormattedMessage id="button.save" />
                  </Typography>
                )}
              </DashboardButton>
            </RowContainer>
          </Grid>
        </ColumnContainer>
      </form>

      {isSuccess ? <div></div> : null}
    </ColumnContainer>
  );
};

export default SecuritySettings;
